import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * TEMPORARY: Phone verification is bypassed while the Twilio A2P 10DLC campaign
 * is pending carrier approval. Flip this flag back to `false` once SMS works.
 */
const PHONE_VERIFICATION_BYPASS = false;

/**
 * Returns whether the currently signed-in user has a verified phone number on file.
 * Used by ProtectedRoute to gate all app access until the user verifies a phone via SMS OTP.
 */
export const usePhoneVerified = () => {
  const { user } = useAuth();
  const [verified, setVerified] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setLoading(false);
      setVerified(null);
      return;
    }

    // Bypass: treat every signed-in user as phone-verified.
    if (PHONE_VERIFICATION_BYPASS) {
      setVerified(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("phone_verified, phone_number")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;

      if (error || !data) {
        setVerified(false);
      } else {
        setVerified(!!(data as any).phone_verified);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return { verified, loading };
};
